/* Premiere ticketing (2-day version) — behaviour.
   Loaded only by premiere-ticketing-2day-block.liquid.

   Built for events sold on TWO (or more) days from one product. Each variant
   carries a day and a tier, e.g.

       option1: "November 16, 2026 10:00 AM"   option2: "Beacon"
       option1: "November 17, 2026 10:00 AM"   option2: "Beacon"

   The script works out which option is the tier (it contains torch / beacon /
   amplifier / guild beacon / general) and treats every OTHER option as the day. Variants are
   grouped by day, a toggle is built from those days, and the cart is always
   sent the variant id for the selected day + tier — so a buyer can never be
   sold the wrong day's ticket. Nothing here is per-product; day names, prices
   and availability all come from the variants.

   The buyer must pick a day before anything can be added to the cart.

   A product with only one day still works: the toggle hides itself and that
   day is selected automatically.

   Separate from premiere-ticketing.js and premiere-ticketing-rename.js: every
   file name and element id carries a "-2day" suffix. See README-2day.md. */

document.addEventListener("DOMContentLoaded", function () {
  const reserveForm = document.getElementById('ticket-reserve-form-2day');

  // The block owns every element below. If it isn't on the page there is
  // nothing to wire up, and continuing would throw on the first lookup.
  if (!reserveForm) return;

  const ticketTypeSelect = document.getElementById('ticket-type-2day');
  const quantityInput = document.getElementById('ticket-quantity-2day');
  const minusBtn = document.getElementById('qty-minus-2day');
  const plusBtn = document.getElementById('qty-plus-2day');
  const ticketCountSpan = reserveForm.querySelector('.ticket-count');
  const totalPriceSpan = reserveForm.querySelector('.total-price');
  const savingsText = reserveForm.querySelector('.savings-text');
  const finalPriceSpan = reserveForm.querySelector('.final-price');
  const cartInputs = document.getElementById('reserve-cart-inputs-2day');
  const productDataElement = document.getElementById('reserve-product-data-2day');
  const reserveBtn = reserveForm.querySelector('.reserve-btn');
  const reserveLabel = reserveForm.querySelector('.reserve-btn__label');
  const reserveSoldOut = reserveForm.querySelector('.reserve-btn__soldout');
  const reserveNeedDay = reserveForm.querySelector('.reserve-btn__needday');
  const reserveCapNote = document.getElementById('reserve-cap-note-2day');

  // Day picker (top of the section) and the day readout in the reserve bar.
  const dayPicker = document.getElementById('day-picker-2day');
  const dayToggle = document.getElementById('day-toggle-2day');
  const dayHint = document.getElementById('day-hint-2day');
  const reserveDay = document.getElementById('reserve-day-2day');
  const reserveDayValue = document.getElementById('reserve-day-value-2day');
  const reserveDayChange = document.getElementById('reserve-day-change-2day');

  // Order matters: applyTierData() falls back to the first tier in this list
  // that is still on sale, so General Admission is deliberately last.
  // guild-beacon (the free Guild Beacon) sits after the paid tiers so the
  // fallback never moves a buyer onto a free ticket they didn't choose.
  const TIERS = ['beacon', 'amplifier', 'torch', 'guild-beacon', 'general'];

  // Tiers that only appear once a day is picked. Before that, their card and
  // dropdown option stay hidden even if some day sells them.
  const DAY_PICK_ONLY = ['guild-beacon'];

  // Per-order cap. Anything not listed uses DEFAULT_MAX_QUANTITY.
  const DEFAULT_MAX_QUANTITY = 20;
  const MAX_QUANTITY = { 'guild-beacon': 1 };

  const TYPE_LABELS = {
    torch: 'Angel Guild',
    beacon: 'Angel Diamond',
    amplifier: 'Angel Amplifier',
    'guild-beacon': 'Free Angel Guild Beacon',
    general: 'General Admission'
  };

  const HINT_DEFAULT = 'Pick a day to see availability and reserve your tickets.';
  const HINT_NUDGE = 'Please choose a day before reserving.';

  // ---- Sold-out override -----------------------------------------------------
  // Set PER PRODUCT in the block:
  //
  //   data-sold-out="torch"                       torch, on every day
  //   data-sold-out="torch@November 17"           torch, on November 17 only
  //   data-sold-out="torch, amplifier@Nov 16"     several, comma separated
  //
  // The text after @ is matched against the day's name as Shopify spells it
  // (capitals don't matter), so "November 17" matches "November 17, 2026 10:00 AM".
  const SOLD_OUT_OVERRIDE = ((productDataElement && productDataElement.getAttribute('data-sold-out')) || '')
    .split(',')
    .map(function (s) { return s.trim().toLowerCase(); })
    .filter(Boolean)
    .map(function (token) {
      const at = token.indexOf('@');
      return at === -1
        ? { tier: token, day: '' }
        : { tier: token.slice(0, at).trim(), day: token.slice(at + 1).trim() };
    });

  function isOverridden(type, day) {
    return SOLD_OUT_OVERRIDE.some(function (o) {
      if (o.tier !== type) return false;
      if (!o.day) return true;
      return !!day && day.key.indexOf(o.day) !== -1;
    });
  }

  // ---- Reading the variants into days ---------------------------------------

  // Shopify reports a variant as unavailable once inventory runs out, but only
  // when the variant actually tracks inventory. An untracked variant is treated
  // as available — which is how Shopify itself behaves.
  function isVariantAvailable(variant) {
    if (!variant) return false;
    if (typeof variant.available === 'boolean') return variant.available;
    if (variant.inventory_management && typeof variant.inventory_quantity === 'number') {
      return variant.inventory_quantity > 0 || variant.inventory_policy === 'continue';
    }
    return true;
  }

  // Exact match first ("Beacon"), then substring ("Beacon Ticket"). Spaces and
  // underscores count as hyphens, so "Guild Beacon" reads as "guild-beacon".
  // The substring pass tries longer keywords first: "guild-beacon" contains
  // "beacon", and must win before the plain Beacon card can claim it.
  // Other Shopify names that mean a tier. "Free Beacon" also contains
  // "beacon", so it must be listed here or the paid Beacon card claims it.
  const TIER_ALIASES = { 'free-beacon': 'guild-beacon' };

  const KEYWORDS_LONGEST_FIRST = TIERS.concat(Object.keys(TIER_ALIASES))
    .sort(function (a, b) { return b.length - a.length; });

  function tierOf(value) {
    const v = String(value).trim().toLowerCase().replace(/[\s_]+/g, '-');
    const hit = KEYWORDS_LONGEST_FIRST.find(function (k) { return v === k; }) ||
                KEYWORDS_LONGEST_FIRST.find(function (k) { return v.indexOf(k) !== -1; }) ||
                null;
    return hit ? (TIER_ALIASES[hit] || hit) : null;
  }

  // A variant's option values. Falls back to splitting the title, which
  // Shopify writes as "Option 1 / Option 2", if the option fields are missing.
  function optionValues(variant) {
    const opts = [variant.option1, variant.option2, variant.option3]
      .filter(function (o) { return o !== null && o !== undefined && String(o).trim() !== ''; })
      .map(String);
    if (opts.length) return opts;
    return variant.title ? String(variant.title).split(' / ') : [];
  }

  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Turns "November 16, 2026 10:00 AM" into { date: "Mon, Nov 16", time:
  // "10:00 AM" }. Parsed by hand rather than with Date.parse(), which reads
  // this format differently across browsers. Anything it can't read is shown
  // exactly as written in Shopify, so a non-date day name still works.
  function formatDay(label) {
    const m = label.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?(?:\s+(\d{4}))?/i);
    const t = label.match(/\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?/i);
    const time = t ? t[1] + ':' + (t[2] || '00') + ' ' + t[3].toUpperCase() + 'M' : '';

    if (!m) return { date: label, short: '', time: '', sortKey: null };

    const month = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase());
    const dayNum = parseInt(m[2], 10);
    const year = m[3] ? parseInt(m[3], 10) : null;

    let weekday = '';
    let sortKey = null;
    if (year) {
      let hours = 0;
      if (t) hours = (parseInt(t[1], 10) % 12) + (t[3].toLowerCase() === 'p' ? 12 : 0);
      const d = new Date(year, month, dayNum, hours, t && t[2] ? parseInt(t[2], 10) : 0);
      weekday = WEEKDAYS[d.getDay()] + ', ';
      sortKey = d.getTime();
    }
    const short = MONTH_LABELS[month] + ' ' + dayNum;
    return { date: weekday + short, short: short, time: time, sortKey: sortKey };
  }

  // days: [{ key, label, date, time, variants: { beacon: {...}, ... } }]
  let days = [];

  try {
    const variantsList = JSON.parse(productDataElement.getAttribute('data-variants') || '[]');
    const byKey = {};

    variantsList.forEach(function (variant) {
      const opts = optionValues(variant);
      const tierIndex = opts.findIndex(function (o) { return tierOf(o) !== null; });
      if (tierIndex === -1) {
        console.warn('Variant has no tier keyword, skipped:', variant.title || variant.id);
        return;
      }
      const tier = tierOf(opts[tierIndex]);
      const label = opts.filter(function (_, i) { return i !== tierIndex; }).join(' ').trim();
      const key = label.toLowerCase();

      if (!byKey[key]) {
        const pretty = formatDay(label);
        byKey[key] = { key: key, label: label, date: pretty.date, short: pretty.short, time: pretty.time, sortKey: pretty.sortKey, variants: {} };
        days.push(byKey[key]);
      }
      if (byKey[key].variants[tier]) {
        console.warn('Two "' + tier + '" variants on "' + label + '" — using the first:', byKey[key].variants[tier].id);
        return;
      }
      byKey[key].variants[tier] = variant;
    });

    // Chronological when every day has a readable date; otherwise keep the
    // order the variants have in the Shopify admin.
    if (days.length > 1 && days.every(function (d) { return d.sortKey !== null; })) {
      days.sort(function (a, b) { return a.sortKey - b.sortKey; });
    }

    console.log('Found days:', days);
  } catch (error) {
    console.error('Error parsing product data:', error);
  }

  // ---- State -----------------------------------------------------------------

  let currentType = 'beacon';
  let currentQuantity = 1;
  // One day: nothing to choose, select it. Several: the buyer must choose.
  let currentDay = days.length === 1 ? days[0] : null;

  function needsDay() {
    return days.length > 1 && !currentDay;
  }

  // Whether this block actually renders the tier. The dropdown option is the
  // marker: no option means the customer has no way to see or choose the tier,
  // so nothing here may select it on their behalf.
  function isTierPresent(type) {
    return !!ticketTypeSelect.querySelector('option[value="' + type + '"]');
  }

  // Is this tier sold on the selected day at all? A day with no variant for a
  // tier (deleted in Shopify) means "not offered", which hides the card —
  // different from a variant that exists but has run out (shows Sold Out).
  // Before a day is picked: offered if ANY day has it. If no variants could be
  // read at all, everything counts as offered so the block never goes blank.
  function isOffered(type) {
    if (days.length === 0) return DAY_PICK_ONLY.indexOf(type) === -1;
    if (currentDay) return !!currentDay.variants[type];
    if (DAY_PICK_ONLY.indexOf(type) !== -1) return false;
    return days.some(function (d) { return !!d.variants[type]; });
  }

  function isSoldOutOn(type, day) {
    if (isOverridden(type, day)) return true;
    return !isVariantAvailable(day && day.variants[type]);
  }

  // With a day chosen: sold out on that day. Before a day is chosen: sold out
  // only if it is sold out on EVERY day, since the buyer may still pick one
  // where it's available.
  function isSoldOut(type) {
    if (currentDay || days.length === 0) return isSoldOutOn(type, currentDay);
    return days.every(function (d) { return isSoldOutOn(type, d); });
  }

  function isDaySoldOut(day) {
    return TIERS.filter(isTierPresent).every(function (t) { return isSoldOutOn(t, day); });
  }

  function selectedVariant() {
    return currentDay ? currentDay.variants[currentType] || null : null;
  }

  // Unit price in dollars. Shopify serialises prices in cents, already in the
  // customer's presentment currency. Before a day is picked, the lowest price
  // across the days is shown (normally both days cost the same).
  //
  // Returns null when there is no variant to read; callers then fall back to
  // the number printed in the HTML rather than showing $0.00.
  function getUnitPrice(type) {
    const pool = currentDay ? [currentDay] : days;
    const prices = pool
      .map(function (d) { return d.variants[type]; })
      .filter(function (v) { return v && typeof v.price === 'number'; })
      .map(function (v) { return v.price / 100; });
    return prices.length ? Math.min.apply(null, prices) : null;
  }

  function getFallbackPrice(type) {
    const el = document.querySelector('[data-tier-price="' + type + '"]');
    const parsed = el ? parseFloat(el.textContent.replace(/[^0-9.]/g, '')) : NaN;
    return isNaN(parsed) ? 0 : parsed;
  }

  function priceFor(type) {
    const fromVariant = getUnitPrice(type);
    return fromVariant === null ? getFallbackPrice(type) : fromVariant;
  }

  function getMaxQuantity(type) {
    return MAX_QUANTITY[type] || DEFAULT_MAX_QUANTITY;
  }

  // Every tier allows a single unit.
  function getMinQuantity(type) {
    return 1;
  }

  function syncQuantityBounds() {
    const min = getMinQuantity(currentType);
    const max = getMaxQuantity(currentType);
    quantityInput.min = min;
    quantityInput.max = max;
    if (currentQuantity < min) currentQuantity = min;
    if (currentQuantity > max) currentQuantity = max;
    quantityInput.value = currentQuantity;
  }

  function dayText(day) {
    return day.date + (day.time ? ' · ' + day.time : '');
  }

  // ---- Day toggle ------------------------------------------------------------

  let dayButtons = [];

  // Optional per-product labels from the block, e.g. { "Nov 17": "Red Carpet
  // Premiere" }. Keys are matched case-insensitively against the short date
  // ("Nov 17") or, for days that aren't dates, the day name as written in
  // Shopify. A typo in the JSON just means no labels — never a broken picker.
  const dayNotes = {};
  try {
    const raw = JSON.parse((dayPicker && dayPicker.getAttribute('data-day-notes')) || '{}');
    Object.keys(raw).forEach(function (k) {
      const note = String(raw[k] || '').trim();
      if (note) dayNotes[k.trim().toLowerCase().replace(/\s+/g, ' ')] = note;
    });
  } catch (error) {
    console.warn('Could not read data-day-notes — check the JSON in the block:', error);
  }

  function noteFor(day) {
    return (day.short && dayNotes[day.short.toLowerCase()]) || dayNotes[day.key] || '';
  }

  function renderDayToggle() {
    if (!dayPicker || !dayToggle) return;

    // Nothing to choose between: hide the picker and the reserve-bar readout.
    if (days.length < 2) {
      dayPicker.hidden = true;
      if (reserveDay) reserveDay.hidden = true;
      return;
    }

    dayToggle.innerHTML = '';
    dayButtons = days.map(function (day, index) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'day-toggle__option';
      btn.setAttribute('role', 'radio');
      btn.dataset.dayIndex = String(index);

      // Radio circle: the universal "pick one of these" signal. Empty until
      // chosen, then filled. Decorative — role="radio" already tells screen
      // readers what it is.
      const radio = document.createElement('span');
      radio.className = 'day-toggle__radio';
      radio.setAttribute('aria-hidden', 'true');
      btn.appendChild(radio);

      const text = document.createElement('span');
      text.className = 'day-toggle__text';
      btn.appendChild(text);

      const date = document.createElement('span');
      date.className = 'day-toggle__date';
      date.textContent = day.date;
      text.appendChild(date);

      const noteText = noteFor(day);
      if (noteText) {
        const note = document.createElement('span');
        note.className = 'day-toggle__note';
        note.textContent = noteText;
        text.appendChild(note);
      }

      const status = document.createElement('span');
      status.className = 'day-toggle__status';
      status.textContent = 'Sold Out';
      text.appendChild(status);
      btn._status = status;

      btn.addEventListener('click', function () { selectDay(day); });
      btn.addEventListener('keydown', function (e) { onDayKeydown(e, index); });

      dayToggle.appendChild(btn);
      return btn;
    });

    paintDayToggle();
  }

  // Arrow keys move between days, as in any radio group.
  function onDayKeydown(e, index) {
    const step = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
               : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    for (let i = 1; i <= days.length; i++) {
      const next = (index + step * i + days.length) % days.length;
      if (!isDaySoldOut(days[next])) {
        selectDay(days[next]);
        if (dayButtons[next].focus) dayButtons[next].focus();
        return;
      }
    }
  }

  function paintDayToggle() {
    const firstOpen = days.findIndex(function (d) { return !isDaySoldOut(d); });

    dayButtons.forEach(function (btn, i) {
      const day = days[i];
      const selected = day === currentDay;
      const soldOut = isDaySoldOut(day);

      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      btn.classList.toggle('is-selected', selected);
      btn.classList.toggle('is-sold-out', soldOut);
      btn.disabled = soldOut;
      btn.setAttribute('aria-label', day.date + (soldOut ? ', sold out' : ''));
      if (btn._status) btn._status.hidden = !soldOut;

      // Roving tabindex: one tab stop for the group, on the selected day (or
      // the first open day before one is chosen).
      const tabStop = currentDay ? selected : i === firstOpen;
      btn.setAttribute('tabindex', tabStop ? '0' : '-1');
    });

    if (dayHint) dayHint.textContent = currentDay ? 'Showing tickets for ' + currentDay.date : HINT_DEFAULT;
  }

  function selectDay(day) {
    if (!day || isDaySoldOut(day)) return;
    currentDay = day;
    if (dayPicker) dayPicker.classList.remove('needs-attention');
    paintDayToggle();
    applyTierData();
    syncQuantityBounds();
    updateDisplay();
  }

  // Draws the eye to the day picker when the buyer tries to reserve, or picks
  // a tier, before choosing a day. Only scrolls if the picker is off screen.
  function nudgeDayPicker() {
    if (!dayPicker) return;
    dayPicker.classList.remove('needs-attention');
    // Force a reflow so the pulse animation restarts on repeat nudges.
    void dayPicker.offsetWidth;
    dayPicker.classList.add('needs-attention');
    if (dayHint) dayHint.textContent = HINT_NUDGE;

    const rect = dayPicker.getBoundingClientRect();
    const onScreen = rect.top >= 0 && rect.bottom <= (window.innerHeight || 0);
    if (!onScreen && typeof dayPicker.scrollIntoView === 'function') {
      dayPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const target = dayButtons.find(function (b) { return b.getAttribute('tabindex') === '0'; });
    if (target && target.focus) target.focus({ preventScroll: true });
  }

  if (reserveDayChange) {
    reserveDayChange.addEventListener('click', function (e) {
      e.preventDefault();
      if (dayPicker && typeof dayPicker.scrollIntoView === 'function') {
        dayPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const target = dayButtons.find(function (b) { return b.getAttribute('tabindex') === '0'; });
      if (target && target.focus) target.focus({ preventScroll: true });
    });
  }

  // ---- Pricing ---------------------------------------------------------------

  function calculatePricing() {
    const unitPrice = priceFor(currentType);
    let totalPrice = 0;
    let savings = 0;

    // Torch is paid per ticket like Beacon and Amplifier (its old "first
    // ticket free" display was removed — Shopify always charged for it).
    if (currentType === 'guild-beacon') {
      // Always shown as free. In Shopify the variant may carry a price (e.g.
      // $100) that an automatic discount removes at checkout, so the page must
      // not show that price as the total. "Save" is the amount the discount
      // takes off: the variant's own price, or the paid Beacon price if the
      // variant is set to $0.
      totalPrice = 0;
      savings = currentQuantity * (unitPrice > 0 ? unitPrice : priceFor('beacon'));
    } else {
      totalPrice = currentQuantity * unitPrice;
    }

    return { totalPrice, savings };
  }

  // Paint the per-tier prices and sold-out treatment onto the cards for the
  // selected day. Runs on load and every time the day changes.
  function applyTierData() {
    TIERS.forEach(function (type) {
      const soldOut = isSoldOut(type);
      const fromVariant = getUnitPrice(type);

      if (fromVariant !== null) {
        document.querySelectorAll('[data-tier-price="' + type + '"]').forEach(function (el) {
          el.textContent = Number.isInteger(fromVariant) ? String(fromVariant) : fromVariant.toFixed(2);
        });
      }

      const offered = isOffered(type);
      const card = document.querySelector('[data-tier-card="' + type + '"]');
      if (card) {
        card.hidden = !offered;
        const ribbon = card.querySelector('[data-sold-out-ribbon]');
        if (ribbon) ribbon.hidden = !(soldOut && offered);

        const cta = card.querySelector('.cta-button[data-ticket-type]');
        if (cta) {
          cta.classList.toggle('is-sold-out', soldOut);
          cta.disabled = soldOut;
          cta.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
          if (soldOut) {
            if (!cta.dataset.labelOriginal) cta.dataset.labelOriginal = cta.textContent;
            cta.textContent = 'Sold Out';
          } else if (cta.dataset.labelOriginal) {
            cta.textContent = cta.dataset.labelOriginal;
          }
        }

        const panel = card.querySelector('.clickable-pricing');
        if (panel) {
          panel.style.pointerEvents = soldOut ? 'none' : '';
          panel.style.opacity = soldOut ? '0.55' : '';
        }
      }

      const option = ticketTypeSelect.querySelector('option[value="' + type + '"]');
      if (option) {
        if (!option.dataset.labelOriginal) option.dataset.labelOriginal = option.textContent;
        // Only a tier that is offered can be "Sold Out". A hidden, not-offered
        // option keeps its plain label: the suffix would never be seen, and
        // browsers size the dropdown to its longest option, hidden ones included.
        option.textContent = option.dataset.labelOriginal + (soldOut && offered ? ' — Sold Out' : '');
        // hidden alone is ignored by Safari for <option>; disabled is not.
        option.hidden = !offered;
        option.disabled = !offered;
      }
    });

    // Let the CSS re-center the grid when fewer than three cards are showing.
    const grid = document.querySelector('.pricing-grid');
    if (grid) {
      const shown = TIERS.filter(function (type) {
        const card = document.querySelector('[data-tier-card="' + type + '"]');
        return card && !card.hidden;
      }).length;
      grid.dataset.visibleCards = String(shown);
    }

    // Never sit on a sold-out tier: move to the first one still for sale that
    // this block actually shows. If nothing is left, stay put and show Sold Out.
    if (isSoldOut(currentType)) {
      const firstAvailable = TIERS.find(function (type) {
        return isTierPresent(type) && !isSoldOut(type);
      });
      if (firstAvailable) {
        currentType = firstAvailable;
        ticketTypeSelect.value = firstAvailable;
      }
    }
  }

  // The reserve button has three states: needs a day, sold out, or normal.
  function updateReserveState() {
    const pickDay = needsDay();
    const soldOut = !pickDay && isSoldOut(currentType);

    if (reserveLabel) reserveLabel.hidden = pickDay || soldOut;
    if (reserveSoldOut) reserveSoldOut.hidden = !soldOut;
    if (reserveNeedDay) reserveNeedDay.hidden = !pickDay;
    // "Limited to 1" hint: only for the free ticket, and only while it's on sale.
    if (reserveCapNote) reserveCapNote.hidden = !(currentType === 'guild-beacon' && !pickDay && !soldOut);

    if (reserveBtn) {
      reserveBtn.classList.toggle('is-sold-out', soldOut);
      reserveBtn.classList.toggle('needs-day', pickDay);
      // Not disabled while a day is needed: clicking it points to the picker.
      reserveBtn.disabled = soldOut;
      reserveBtn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
    }

    const lockQty = isSoldOut(currentType);
    minusBtn.disabled = lockQty;
    plusBtn.disabled = lockQty;
    quantityInput.disabled = lockQty;

    if (reserveDay && reserveDayValue) {
      reserveDayValue.textContent = currentDay ? dayText(currentDay) : 'Choose a day';
      reserveDay.classList.toggle('is-empty', !currentDay);
    }
  }

  function updateDisplay() {
    const { totalPrice, savings } = calculatePricing();

    const ticketText = currentQuantity === 1 ? 'Ticket' : 'Tickets';
    ticketCountSpan.textContent = `${currentQuantity} ${ticketText}`;

    totalPriceSpan.textContent = `$${totalPrice.toFixed(2)}`;
    finalPriceSpan.textContent = totalPrice.toFixed(2);

    if (savings > 0) {
      savingsText.style.display = 'block';
      savingsText.textContent = `Save $${savings}`;
    } else {
      savingsText.style.display = 'none';
    }

    updateReserveState();
    updateCartInputs();
  }

  // The hidden inputs the form posts to /cart/add. Only ever the variant for
  // the SELECTED day and tier — with no day chosen, nothing is posted.
  function updateCartInputs() {
    cartInputs.innerHTML = '';

    if (needsDay() || isSoldOut(currentType) || currentQuantity <= 0) return;

    const variant = selectedVariant();
    if (!variant) {
      console.error(`No variant found for ${currentType} on ${currentDay ? currentDay.label : '(no day)'}`);
      return;
    }

    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = variant.id;
    cartInputs.appendChild(idInput);

    const qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = currentQuantity;
    cartInputs.appendChild(qtyInput);

    console.log('Cart inputs updated:', {
      day: currentDay.label,
      type: currentType,
      variantId: variant.id,
      sku: variant.sku,
      quantity: currentQuantity
    });
  }

  function handleTicketSelection(type, quantity) {
    currentType = type;
    currentQuantity = Math.max(quantity, getMinQuantity(type));

    ticketTypeSelect.value = type;
    quantityInput.value = currentQuantity;

    syncQuantityBounds();
    updateDisplay();

    if (needsDay()) nudgeDayPicker();
  }

  // ---- Animation -------------------------------------------------------------

  const CART_ICON_SELECTORS = [
    '#cart-icon-bubble',
    '[data-cart-icon]',
    '.header__icon--cart',
    '.site-header__cart',
    '.header-cart',
    '.cart-link',
    'a[href$="/cart"]',
    'a[href*="/cart"]'
  ];

  function getCartTarget() {
    for (const selector of CART_ICON_SELECTORS) {
      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) return { el: el, rect: rect };
      }
    }
    return {
      el: null,
      rect: { left: window.innerWidth - 80, top: 20, width: 40, height: 40 }
    };
  }

  function animateTicketToCart(sourceElement, label, options) {
    options = options || {};

    const flyingTicket = document.createElement('div');
    flyingTicket.className = 'flying-ticket';
    flyingTicket.textContent = label || 'Ticket';

    const sourceRect = sourceElement.getBoundingClientRect();
    const reserveBar = document.querySelector('.event-reserve-cta');
    const targetElement = options.targetEl !== undefined ? options.targetEl : reserveBar;
    const targetRect = options.targetRect || (reserveBar && reserveBar.getBoundingClientRect());

    flyingTicket.style.left = (sourceRect.left + sourceRect.width / 2 - 50) + 'px';
    flyingTicket.style.top = (sourceRect.top + sourceRect.height / 2 - 20) + 'px';

    document.body.appendChild(flyingTicket);

    setTimeout(() => {
      if (targetRect) {
        flyingTicket.style.left = (targetRect.left + targetRect.width / 2 - 50) + 'px';
        flyingTicket.style.top = (targetRect.top + 10) + 'px';
      }
      flyingTicket.classList.add('animate');
    }, 50);

    setTimeout(() => {
      const flashClass = options.pop ? 'cart-icon-pop' : 'reserve-highlight';
      if (targetElement) targetElement.classList.add(flashClass);
      if (document.body.contains(flyingTicket)) document.body.removeChild(flyingTicket);

      setTimeout(() => {
        if (targetElement) targetElement.classList.remove(flashClass);
      }, 600);

      if (typeof options.onComplete === 'function') options.onComplete();
    }, 850);
  }

  // ---- Card clicks -----------------------------------------------------------

  const CARD_LABELS = {
    torch: 'Angel Guild',
    beacon: 'Angel Single Ticket',
    amplifier: 'Angel Amplifier Ticket',
    'guild-beacon': 'Free Angel Guild Beacon',
    general: 'General Admission'
  };

  function pickFromCard(type, sourceEl) {
    if (!type || isSoldOut(type)) return;
    // Before a day is chosen the ticket doesn't fly to the reserve bar — the
    // nudge in handleTicketSelection() points at the day picker instead.
    if (!needsDay()) animateTicketToCart(sourceEl || document.body, CARD_LABELS[type]);
    handleTicketSelection(type, 1);
  }

  // Window-level handlers used by the pricing panels' inline onclicks.
  // Each looks up the panel inside its OWN card: Torch's panel reuses the
  // .diamond-pricing class and comes first on the page.
  function panelOf(type) {
    return document.querySelector('[data-tier-card="' + type + '"] .clickable-pricing');
  }
  window.handleTorchPricingClick2Day = function () {
    pickFromCard('torch', panelOf('torch'));
  };
  window.handleDIAMONDPricingClick2Day = function () {
    pickFromCard('beacon', panelOf('beacon'));
  };
  window.handleGroupPricingClick2Day = function () {
    pickFromCard('amplifier', panelOf('amplifier'));
  };
  window.handleGeneralPricingClick2Day = function () {
    pickFromCard('general', document.querySelector('.ga-pricing'));
  };

  document.addEventListener('click', function (e) {
    if (!e.target.matches('.cta-button')) return;
    e.preventDefault();

    // Routing uses data-ticket-type, so the visible button copy can change
    // freely without breaking which variant is bought.
    const type = e.target.getAttribute('data-ticket-type');
    if (TIERS.indexOf(type) !== -1) pickFromCard(type, e.target);
  });

  // ---- Quantity and dropdown -------------------------------------------------

  minusBtn.addEventListener('click', function (e) {
    e.preventDefault();
    if (isSoldOut(currentType)) return;
    if (currentQuantity > getMinQuantity(currentType)) {
      currentQuantity--;
      quantityInput.value = currentQuantity;
      updateDisplay();
    }
  });

  plusBtn.addEventListener('click', function (e) {
    e.preventDefault();
    if (isSoldOut(currentType)) return;
    if (currentQuantity < getMaxQuantity(currentType)) {
      currentQuantity++;
      quantityInput.value = currentQuantity;
      updateDisplay();
    }
  });

  quantityInput.addEventListener('input', function () {
    if (isSoldOut(currentType)) return;
    const min = getMinQuantity(currentType);
    const max = getMaxQuantity(currentType);
    const newQuantity = parseInt(this.value) || min;
    if (newQuantity >= min && newQuantity <= max) {
      currentQuantity = newQuantity;
      updateDisplay();
    }
  });

  ticketTypeSelect.addEventListener('change', function () {
    currentType = this.value;
    syncQuantityBounds();
    updateDisplay();
  });

  // ---- Add-to-cart confirmation ----------------------------------------------
  // A capture-phase listener on `document` runs before any theme handler bound
  // further down the tree, so the feedback can't be pre-empted by a theme that
  // intercepts the form submit for its own AJAX add.

  const cartToast = document.getElementById('cart-toast-2day');
  const cartToastDetail = document.getElementById('cart-toast-detail-2day');
  const cartToastClose = document.getElementById('cart-toast-close-2day');
  let toastTimer = null;

  function hideToast() {
    if (cartToast) cartToast.classList.remove('is-visible');
  }

  function showToast(detailText) {
    if (!cartToast) return;
    if (cartToastDetail) cartToastDetail.textContent = detailText || '';
    cartToast.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  }

  if (cartToastClose) {
    cartToastClose.addEventListener('click', function () {
      if (toastTimer) clearTimeout(toastTimer);
      hideToast();
    });
  }

  function fetchCartCount() {
    if (typeof fetch !== 'function') return Promise.resolve(null);
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        return (data && typeof data.item_count === 'number') ? data.item_count : null;
      })
      .catch(function () { return null; });
  }

  let knownCartCount = null;
  fetchCartCount().then(function (count) { knownCartCount = count; });

  function confirmCartCount(attempt) {
    attempt = attempt || 1;
    const baseline = knownCartCount;

    fetchCartCount().then(function (count) {
      if (count !== null && (baseline === null || count > baseline)) {
        knownCartCount = count;
        const noun = count === 1 ? 'item' : 'items';
        if (cartToast && cartToast.classList.contains('is-visible') && cartToastDetail) {
          cartToastDetail.textContent += ` · ${count} ${noun} in cart`;
        }
        return;
      }
      if (attempt < 8) setTimeout(function () { confirmCartCount(attempt + 1); }, 500);
    });
  }

  function triggerReserveFeedback(sourceEl) {
    let label = `${currentQuantity} × ${TYPE_LABELS[currentType] || 'Ticket'}`;
    // The toast repeats the day so the buyer can confirm they got the right one.
    if (currentDay && days.length > 1) label += ` · ${dayText(currentDay)}`;

    if (sourceEl) {
      sourceEl.classList.add('is-adding');
      setTimeout(function () { sourceEl.classList.remove('is-adding'); }, 850);
    }

    const target = getCartTarget();
    animateTicketToCart(sourceEl || reserveForm, label, {
      targetRect: target.rect,
      targetEl: target.el,
      pop: true
    });

    showToast(label);
    confirmCartCount();
  }

  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest ? e.target.closest('.reserve-btn') : null;
    if (!btn || !reserveForm.contains(btn)) return;
    if (needsDay()) {
      // Stop the post here: the theme may add to cart via its own handler.
      e.preventDefault();
      e.stopPropagation();
      nudgeDayPicker();
      return;
    }
    if (currentQuantity === 0 || isSoldOut(currentType)) return;
    triggerReserveFeedback(btn);
  }, true);

  reserveForm.addEventListener('submit', function (e) {
    // Last line of defence: a keyboard Enter, or a theme script that clears
    // the button's disabled attribute, must still not reach /cart/add without
    // a day and an on-sale tier.
    if (needsDay()) {
      e.preventDefault();
      nudgeDayPicker();
      return;
    }
    if (isSoldOut(currentType)) {
      e.preventDefault();
      return;
    }
    if (currentQuantity === 0) {
      e.preventDefault();
      alert('Please select at least one ticket.');
      return;
    }
    if (!cartToast || !cartToast.classList.contains('is-visible')) {
      triggerReserveFeedback(reserveForm.querySelector('.reserve-btn'));
    }
  });

  // ---- Initialise ------------------------------------------------------------
  renderDayToggle();
  applyTierData();
  syncQuantityBounds();
  updateDisplay();
});
